import { useMemo, useRef, useCallback } from "react";
import { TFormItem } from "formifex";
import type { TDisplayConditionItem } from "formifex/dist/types/form";

interface ArrayFieldConfig {
	fields: string[];
	conditional?: Record<string, (item: any, reportType?: string) => boolean>;
	groupRequired?: string[][];
}

const ARRAY_REQUIRED_FIELDS: Record<string, ArrayFieldConfig> = {
	follow_up_actions: {
		fields: [
			"action_type",
			"description",
			"responsible",
			"deadline",
			"closing_comment",
		],
		conditional: {
			action_type: (_item, reportType) =>
				!reportType || !["moc", "ofi", "non_conformance"].includes(reportType),
			responsible: (item) => item?.already_completed !== true,
			deadline: (item) => item?.already_completed !== true,
			closing_comment: (item) =>
				item?.status === "completed" || item?.already_completed === true,
		},
	},
	findings: {
		fields: [
			"finding_type",
			"criticality",
			"findings_description",
			"recommended_actions",
			"responsible",
			"deadline",
			"what_was_done",
		],
		conditional: {
			criticality: (item) => item?.finding_type === "Non-Conformity",
			responsible: (item) => item?.already_completed !== true,
			deadline: (item) => item?.already_completed !== true,
			what_was_done: (item) =>
				item?.status === "completed" || item?.already_completed === true,
		},
	},
	causes: {
		fields: ["cause_description"],
		groupRequired: [["human", "technical", "organisational"]],
	},
};

function evaluateCondition(
	condition: TDisplayConditionItem,
	formData: any
): boolean {
	if (typeof condition === "function") {
		return (condition as any)({ formRef: undefined, data: formData });
	}

	const { field, value, operator = "=" } = condition;
	const fieldValue = formData?.[field];

	// Handle strict equality operators that might exist at runtime but aren't in the type
	if ((operator as string) === "===") return fieldValue === value;
	if ((operator as string) === "!==") return fieldValue !== value;

	switch (operator) {
		case "=":
			return fieldValue == value;
		case "!=":
			return fieldValue != value;
		case ">":
			return value !== undefined && fieldValue > value;
		case "<":
			return value !== undefined && fieldValue < value;
		case ">=":
			return value !== undefined && fieldValue >= value;
		case "<=":
			return value !== undefined && fieldValue <= value;
		case "includes":
			if (Array.isArray(fieldValue)) return fieldValue.includes(value);
			if (typeof fieldValue === "string")
				return fieldValue.includes(String(value));
			return false;
		default:
			return true;
	}
}

function shouldDisplay(item: TFormItem, formData: any): boolean {
	if (!item.displayWhen || item.displayWhen.length === 0) return true;
	return item.displayWhen.every((condition) =>
		evaluateCondition(condition, formData)
	);
}

function isFieldFilled(value: any): boolean {
	if (value === undefined || value === null) return false;
	if (typeof value === "string" && value.trim() === "") return false;
	if (Array.isArray(value) && value.length === 0) return false;
	return true;
}

function getNestedValue(obj: any, path: string): any {
	if (!obj || !path) return undefined;
	if (!path.includes(".")) return obj[path];
	return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

function parseSchemaItems(
	items: TFormItem[] | undefined,
	formData: any,
	parentPath: string = "",
	reportType?: string
): string[] {
	if (!items || !Array.isArray(items)) return [];

	const requiredFields: string[] = [];

	for (const item of items) {
		// Skip if item doesn't exist or shouldn't be displayed
		if (!item) continue;

		// Check displayWhen
		if (!shouldDisplay(item, formData)) continue;

		// Handle container types with children
		if (
			item.type === "section" ||
			item.type === "flex" ||
			item.type === "grid"
		) {
			const children = "children" in item ? (item as any).children : undefined;
			if (children) {
				requiredFields.push(
					...parseSchemaItems(children, formData, parentPath, reportType)
				);
			}
			continue;
		}

		// Skip custom fields unless they explicitly opt in via `required: true`
		// and declare the underlying form field name via `fieldName`.
		if (item.type === "custom") {
			const customItem = item as any;
			if (customItem.required === true && customItem.fieldName) {
				const fieldPath = parentPath
					? `${parentPath}.${customItem.fieldName}`
					: customItem.fieldName;
				requiredFields.push(fieldPath);
			}
			continue;
		}

		// Check if it's an input field
		if (item.type === "input") {
			const inputItem = item as any;

			// Skip hidden input type
			if (inputItem.inputType === "hidden") continue;

			// Skip readOnly fields
			if (inputItem.readOnly === true) continue;

			// Skip switches — they always hold a boolean once rendered,
			// so they can never be "unfilled".
			if (inputItem.inputType === "switch") continue;

			// Check if required
			if (inputItem.required === true) {
				const fieldPath = parentPath
					? `${parentPath}.${inputItem.id}`
					: inputItem.id;
				requiredFields.push(fieldPath);
			}
		}
	}

	return requiredFields;
}

function getArrayFieldMetadata(
	items: TFormItem[] | undefined,
	formData: any
): Map<string, { required: boolean }> {
	const metadata = new Map<string, { required: boolean }>();

	function walk(formItems: TFormItem[] | undefined) {
		if (!formItems || !Array.isArray(formItems)) return;

		for (const item of formItems) {
			if (!item) continue;
			if (!shouldDisplay(item, formData)) continue;

			if (
				item.type === "section" ||
				item.type === "flex" ||
				item.type === "grid"
			) {
				const children =
					"children" in item ? (item as any).children : undefined;
				if (children) walk(children);
				continue;
			}

			if (item.type === "custom") {
				const customItem = item as any;
				if (customItem.fieldName) {
					metadata.set(customItem.fieldName, {
						required: customItem.required === true,
					});
				}
			}
		}
	}

	walk(items);
	return metadata;
}

function getArrayUnfilledFields(
	arrayField: string,
	arrayData: any[],
	reportType?: string
): string[] {
	if (!arrayData || !Array.isArray(arrayData) || arrayData.length === 0) {
		// If array is empty, all required fields of first item are unfilled
		const config = ARRAY_REQUIRED_FIELDS[arrayField];
		if (!config) return [];
		const fields = config.fields.map((field) => `${arrayField}[0].${field}`);
		const groupFields = (config.groupRequired || []).map(
			(group) => `${arrayField}[0].${group[0]}`
		);
		return [...fields, ...groupFields];
	}

	const config = ARRAY_REQUIRED_FIELDS[arrayField];
	if (!config) return [];

	const unfilled: string[] = [];

	arrayData.forEach((item, index) => {
		for (const field of config.fields) {
			// Check conditional
			if (config.conditional?.[field]) {
				if (!config.conditional[field](item, reportType)) continue;
			}

			// Check if field is filled
			if (!isFieldFilled(item?.[field])) {
				unfilled.push(`${arrayField}[${index}].${field}`);
			}
		}

		// Check group required fields (at least one in each group must be filled)
		if (config.groupRequired) {
			for (const group of config.groupRequired) {
				const hasAnyFilled = group.some((field) =>
					isFieldFilled(item?.[field])
				);
				if (!hasAnyFilled) {
					unfilled.push(`${arrayField}[${index}].${group[0]}`);
				}
			}
		}
	});

	return unfilled;
}

function expandCollapsePanel(fieldPath: string) {
	// Try to find the DOM element for this field
	// For array fields, we need to find the specific input
	const selectors = [
		`[name="${fieldPath}"]`,
		`[id="${fieldPath}"]`,
		`input[name="${fieldPath}"]`,
		`textarea[name="${fieldPath}"]`,
		`[data-field="${fieldPath}"]`,
	];

	let element: Element | null = null;
	for (const selector of selectors) {
		element = document.querySelector(selector);
		if (element) break;
	}

	if (!element) {
		// For array fields, try to find by looking for inputs within collapse panels
		if (fieldPath.includes("[")) {
			// Extract array field and index
			const match = fieldPath.match(/^(\w+)\[(\d+)\]\.(.+)$/);
			if (match) {
				const [, _arrayName, index, subField] = match;
				// Look for all collapse panels and find the one matching this index
				const panels = document.querySelectorAll(".ant-collapse-item");
				if (panels[Number(index)]) {
					const panel = panels[Number(index)];
					// Check if panel is collapsed
					if (!panel.classList.contains("ant-collapse-item-active")) {
						const header = panel.querySelector(".ant-collapse-header");
						if (header) {
							(header as HTMLElement).click();
						}
					}
					// Now try to find the element within this panel
					const inputs = panel.querySelectorAll(`input, textarea, select`);
					for (const input of inputs) {
						const name = input.getAttribute("name") || "";
						if (name.includes(subField)) {
							element = input;
							break;
						}
					}
				}
			}
		}
	}

	// If still no element, try to find and expand any parent collapse
	if (!element) {
		// Last resort: try to find any collapse panel that might contain this field
		const allPanels = document.querySelectorAll(".ant-collapse-item");
		for (const panel of allPanels) {
			if (!panel.classList.contains("ant-collapse-item-active")) {
				const header = panel.querySelector(".ant-collapse-header");
				if (header) {
					(header as HTMLElement).click();
				}
			}
		}
		// Try finding element again after expanding
		for (const selector of selectors) {
			element = document.querySelector(selector);
			if (element) break;
		}
	}

	return element;
}

export interface UseRequiredFieldsResult {
	unfilledCount: number;
	unfilledFields: string[];
	isComplete: boolean;
	scrollToNextUnfilled: () => void;
}

function useRequiredFields(
	form: any,
	reportType?: string
): UseRequiredFieldsResult {
	const currentIndexRef = useRef(0);

	const result = useMemo(() => {
		const schema = form?.schema;
		const formData = form?.formData;

		if (!schema || !formData) {
			return {
				unfilledCount: 0,
				unfilledFields: [],
				isComplete: false,
			};
		}

		// Parse schema to get required field paths
		const schemaRequiredFields = parseSchemaItems(
			schema.items,
			formData,
			"",
			reportType
		);

		// Determine which array fields are explicitly marked required in the schema.
		// If a custom array field declares `fieldName` without `required`, it is
		// treated as optional. If there is no schema metadata, the existing
		// required-by-default behavior is preserved.
		const arrayFieldMetadata = getArrayFieldMetadata(schema.items, formData);
		const arrayFields = ["follow_up_actions", "findings", "causes"];
		const arrayFieldNames = new Set(arrayFields);

		// Check regular required fields (exclude array field names to avoid
		// double-counting with the array-specific logic below).
		const unfilledRegularFields: string[] = [];
		for (const fieldPath of schemaRequiredFields) {
			if (arrayFieldNames.has(fieldPath)) continue;
			const value = getNestedValue(formData, fieldPath);
			if (!isFieldFilled(value)) {
				unfilledRegularFields.push(fieldPath);
			}
		}

		// Check array fields
		const unfilledArrayFields: string[] = [];

		for (const arrayField of arrayFields) {
			const arrayData = formData?.[arrayField];
			if (arrayData !== undefined) {
				// Only check if the array field exists in formData
				const meta = arrayFieldMetadata.get(arrayField);
				const isRequired = meta ? meta.required : true;

				// Optional empty arrays have no unfilled fields
				if (Array.isArray(arrayData) && arrayData.length === 0 && !isRequired) {
					continue;
				}

				const unfilled = getArrayUnfilledFields(
					arrayField,
					arrayData,
					reportType
				);
				unfilledArrayFields.push(...unfilled);
			}
		}

		const allUnfilled = [...unfilledRegularFields, ...unfilledArrayFields];

		return {
			unfilledCount: allUnfilled.length,
			unfilledFields: allUnfilled,
			isComplete: allUnfilled.length === 0 && schemaRequiredFields.length > 0,
		};
	}, [form?.schema, form?.formData, reportType]);

	const scrollToNextUnfilled = useCallback(() => {
		const { unfilledFields } = result;
		if (unfilledFields.length === 0) return;

		const index = currentIndexRef.current % unfilledFields.length;
		const fieldPath = unfilledFields[index];

		// Try using Ant Design's scrollToField first
		try {
			if (form?.formRef?.scrollToField) {
				// For regular fields, this should work
				if (!fieldPath.includes("[")) {
					form.formRef.scrollToField(fieldPath, {
						behavior: "smooth",
						block: "center",
					});
					currentIndexRef.current = index + 1;
					return;
				}
			}
		} catch {
			// Fallback to manual scrolling
		}

		// Manual scrolling for array fields or fallback
		const element = expandCollapsePanel(fieldPath);
		if (element) {
			element.scrollIntoView({ behavior: "smooth", block: "center" });
			// Focus the element
			const focusable = element.querySelector(
				"input, textarea, select"
			) as HTMLElement;
			if (focusable) {
				focusable.focus();
			} else if (element instanceof HTMLElement) {
				element.focus();
			}
		}

		currentIndexRef.current = index + 1;
	}, [result, form]);

	return {
		...result,
		scrollToNextUnfilled,
	};
}

export default useRequiredFields;
